import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import Avatar from "../common/Avatar";
import AnonSilhouetteAvatar from "./AnonSilhouetteAvatar";
import FlagImg from "../agent-tender/FlagImg";
import { UrgencyPill } from "./LmPills";
import { LockGlyph, AgencyBadge, ShareGlyph } from "./lmGlyphs";
import ClubAddToRadarButton from "../club-radar/profile/ClubAddToRadarButton";
import { canUseClubRadar } from "../../utils/clubRoles";
import {
  formatMarketValueExact,
  formatMarketValueRange,
  formatSalaryBand,
  daysLeftShort,
} from "../../utils/lastMinute";
import { formatDistanceToNowStrict } from "date-fns";

// Quick view (design 9f) — the loupe target: the card's FULL text plus a compact
// spec grid, in the artboard's blue-ring panel. Chrome behavior kept from the
// previous build (portal + ESC + body-scroll-lock + caller-supplied primary) and
// STAYS anonymity-safe: an anonymized listing shows position/age/MV-range only,
// never a name or current club (Invariant 3).
const timeAgoShort = (iso) => {
  try {
    return `${formatDistanceToNowStrict(new Date(iso))} ago`
      .replace(/ minutes?/, "m").replace(/ hours?/, "h").replace(/ days?/, "d")
      .replace(/ months?/, "mo").replace(/ years?/, "y").replace(/ seconds?/, "s");
  } catch (e) {
    return "";
  }
};

// One spec row: label left (#8A98AC), value right (600/#33475E) — artboard 9f.
const SpecRow = ({ label, value }) =>
  value ? (
    <div className="flex items-center justify-between gap-3 text-[12.5px]">
      <span className="text-[#8A98AC]">{label}</span>
      <span className="font-semibold text-[#33475E] text-right">{value}</span>
    </div>
  ) : null;

const LmQuickViewModal = ({ isOpen, onClose, listing, primaryAction = null, onShare = null }) => {
  // Club Add-to-Radar (R10 wiring, unchanged): only for club users, only when the
  // identity is revealed (named + player id). Rendered in the artboard's footer
  // action row — same component, same gate, artboard placement.
  const { currentRole, permissions } = useSelector((state) => state.auth);
  const canRadar = canUseClubRadar(currentRole, permissions);
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || !listing) return null;

  const named = !!listing.is_named;
  const headline = named
    ? listing.player_name || "Player"
    : [listing.position, listing.age].filter((v) => v !== null && v !== undefined && v !== "").join(", ");
  const mv = named ? formatMarketValueExact(listing.market_value) : formatMarketValueRange(listing.market_value);
  const salary = formatSalaryBand(listing);
  const agent = listing.agent || {};
  const metaLine = [
    listing.position,
    listing.age != null ? `${listing.age} yrs` : null,
    mv,
    named ? listing.player_club : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const listed = [listing.created_at ? timeAgoShort(listing.created_at) : "", daysLeftShort(listing.expires_at)]
    .filter(Boolean)
    .join(" · ");
  const nationalities = Array.isArray(listing.nationalities) ? listing.nationalities : [];
  const radarEligible = canRadar && named && (listing.ws_player_id != null || listing.player_id != null);

  const overlay = (
    <div
      className="fixed inset-0 bg-[rgba(16,26,40,0.44)] z-[60] flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Quick view"
    >
      <div
        className="bg-white w-full sm:max-w-[460px] rounded-t-[18px] sm:rounded-[18px] max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden p-[18px] sm:p-[22px] gap-3.5"
        style={{ border: "1px solid #BDD9FF", boxShadow: "0 0 0 3px rgba(34,131,255,.09), 0 24px 48px -16px rgba(16,24,40,.3)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header line: QUICK VIEW · ✕ */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[11px] font-bold uppercase tracking-[0.09em] text-[#1466D8]">Quick view</span>
          <button
            onClick={onClose}
            className="ml-auto text-[#98A5B8] text-[14px] leading-none p-1 -m-1 hover:text-[#14263D]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col gap-3.5 min-h-0">
          {/* Subject row */}
          <div className="flex items-start gap-3">
            {named ? (
              <Avatar src={null} alt={headline} fallbackText={headline} size={56} className="flex-none" />
            ) : (
              <AnonSilhouetteAvatar size={56} />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-[7px] min-w-0">
                <h2 className="text-[17px] font-bold text-[#14263D] truncate">{headline}</h2>
                {named &&
                  nationalities.map((n) => (
                    <FlagImg key={n.code || n.name || n} country={n.code || n.name || n} className="w-[17px] h-3 rounded-[2.5px]" />
                  ))}
              </div>
              <p className="mt-[3px] text-[12.5px] font-medium text-[#5A6B82]">
                {named ? metaLine : "Anonymized profile"}
              </p>
              {!named && metaLine && <p className="mt-[2px] text-[12px] text-[#8A98AC]">{metaLine}</p>}
            </div>
            <span className="flex-shrink-0">
              <UrgencyPill urgency={listing.urgency_class} />
            </span>
          </div>

          {/* Full note (the loupe's whole point) */}
          {listing.note && (
            <div className="rounded-xl bg-[#F8FAFC] border border-[#EEF1F6] px-3.5 py-3 text-[13px] text-[#33475E] leading-[1.6]">
              “{listing.note}”
            </div>
          )}

          {/* Spec grid — 2 columns, label/value rows (9f) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-[18px]">
            <SpecRow label="Positions" value={listing.position} />
            <SpecRow label="Age" value={listing.age != null ? `${listing.age} yrs` : null} />
            <SpecRow label="Salary band" value={salary} />
            <SpecRow label="Market value" value={mv ? mv.replace(/^MV\s*/, "") : null} />
            <SpecRow label="Listed" value={listed} />
          </div>

          {listing.not_ready_for?.length > 0 && (
            <p className="text-[12px] text-[#8A98AC]">
              Not ready for: {listing.not_ready_for.map((c) => c.name).join(" · ")}
            </p>
          )}
          {!named && (
            <p className="flex items-center gap-[7px] text-[12px] text-[#8A98AC]">
              <LockGlyph size="card" />
              Name revealed on interest
            </p>
          )}

          {/* Divider + agent row */}
          <div className="h-px bg-[#EEF1F6] mt-auto" />
          <div className="flex items-center gap-2">
            <Avatar src={null} fallbackText={agent.name} size={24} />
            <span className="text-[12.5px] font-semibold text-[#33475E] truncate">{agent.name}</span>
            <AgencyBadge agency={agent.agency} />
            {agent.agency && <span className="text-[12px] text-[#8A98AC] truncate">{agent.agency}</span>}
          </div>
        </div>

        {/* Footer 3-slot action row (identical placement — 9f). The primary closes
            the quick view as it fires (M3 review F5) so a modal it opens is never
            hidden behind this higher-z overlay. */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {primaryAction && (
            <div className="flex-[1.3] min-w-0" onClickCapture={onClose}>
              {primaryAction}
            </div>
          )}
          {radarEligible && (
            <div className="flex-1 min-w-0 flex justify-center">
              <ClubAddToRadarButton
                wsPlayerId={listing.ws_player_id}
                playerId={listing.ws_player_id == null ? listing.player_id : undefined}
                playerName={listing.player_name}
              />
            </div>
          )}
          {onShare && (
            <button
              type="button"
              onClick={() => onShare(listing)}
              className="flex-none w-[38px] self-stretch min-h-[38px] inline-flex items-center justify-center rounded-[10px] border border-[#D8E0EA] bg-white hover:bg-gray-50"
              aria-label="Share listing"
              title="Share"
            >
              <ShareGlyph />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default LmQuickViewModal;
